import { DAYS_PER_WEEK, addDays, assertCalendarDate, calendarDate, toEpochDay, } from '../date/index.js';
/** ISO day-of-week numbers: Monday is 1, Sunday is 7. */
export const ISO_MONDAY = 1;
/** See {@link ISO_MONDAY}. */
export const ISO_THURSDAY = 4;
/** See {@link ISO_MONDAY}. */
export const ISO_SUNDAY = 7;
/**
 * ISO day of week: 1 (Monday) through 7 (Sunday).
 *
 * Epoch day 0 is 1970-01-01, a Thursday, hence the `+ 3` offset that maps it to
 * ISO 4.
 */
export function isoDayOfWeek(date) {
    const epochDay = toEpochDay(date);
    return (((epochDay + 3) % DAYS_PER_WEEK) + DAYS_PER_WEEK) % DAYS_PER_WEEK + 1;
}
/** The Monday of the ISO week containing `date`. */
export function startOfIsoWeek(date) {
    return addDays(assertCalendarDate(date), ISO_MONDAY - isoDayOfWeek(date));
}
/** The Sunday of the ISO week containing `date`. */
export function endOfIsoWeek(date) {
    return addDays(startOfIsoWeek(date), DAYS_PER_WEEK - 1);
}
/**
 * The Monday on which ISO week 1 of `isoYear` starts.
 *
 * Derived from the 4 January rule, so it lands in December of `isoYear - 1`
 * whenever 1 January falls on a Friday, Saturday or Sunday.
 */
export function startOfIsoWeekYear(isoYear) {
    if (!Number.isInteger(isoYear)) {
        throw new RangeError(`ISO year must be an integer, received ${String(isoYear)}`);
    }
    return startOfIsoWeek(calendarDate(isoYear, 1, 4));
}
/**
 * The Sunday on which the last ISO week of `isoYear` ends.
 *
 * That is the day before week 1 of the following ISO year begins, which is what
 * makes the ISO years a partition of the days with no gap and no overlap.
 */
export function endOfIsoWeekYear(isoYear) {
    return addDays(startOfIsoWeekYear(isoYear + 1), -1);
}
/**
 * Number of ISO weeks in `isoYear`: 52, or 53 in a long year.
 *
 * Computed as the distance between consecutive week-1 Mondays rather than from
 * a weekday formula, so it cannot disagree with {@link startOfIsoWeekYear} - the
 * function the grid is actually built from.
 */
export function isoWeeksInYear(isoYear) {
    const days = toEpochDay(startOfIsoWeekYear(isoYear + 1)) - toEpochDay(startOfIsoWeekYear(isoYear));
    const weeks = days / DAYS_PER_WEEK;
    if (weeks !== 52 && weeks !== 53) {
        // Unreachable for any integer year: consecutive ISO years differ by 52 or
        // 53 whole weeks by construction. Guarded because the return type promises it.
        throw new RangeError(`ISO year ${String(isoYear)} yielded ${String(weeks)} weeks`);
    }
    return weeks;
}
/** Whether `isoYear` is a long ISO year of 53 weeks. */
export function isLongIsoYear(isoYear) {
    return isoWeeksInYear(isoYear) === 53;
}
/**
 * The ISO week-based year and week number `date` belongs to.
 *
 * The week's Thursday decides the year, so a date in late December can carry
 * week 1 of the next ISO year and a date in early January can carry week 52 or
 * 53 of the previous one.
 */
export function isoWeekOf(date) {
    const monday = startOfIsoWeek(date);
    const isoYear = addDays(monday, ISO_THURSDAY - ISO_MONDAY).year;
    const weekOffset = toEpochDay(monday) - toEpochDay(startOfIsoWeekYear(isoYear));
    return { isoYear, isoWeek: weekOffset / DAYS_PER_WEEK + 1 };
}
/** The Monday of ISO week `isoWeek` of `isoYear`. Throws when that week does not exist. */
export function startOfIsoWeekNumber(reference) {
    const { isoYear, isoWeek } = reference;
    const weeks = isoWeeksInYear(isoYear);
    if (!Number.isInteger(isoWeek) || isoWeek < 1 || isoWeek > weeks) {
        throw new RangeError(`ISO year ${String(isoYear)} has weeks 1-${String(weeks)}, received ${String(isoWeek)}`);
    }
    return addDays(startOfIsoWeekYear(isoYear), (isoWeek - 1) * DAYS_PER_WEEK);
}
/** Whether both references name the same ISO week. */
export function isoWeekReferencesEqual(a, b) {
    return a.isoYear === b.isoYear && a.isoWeek === b.isoWeek;
}
//# sourceMappingURL=iso-week.js.map